import { appUI } from 'lib/app-ui';
import { nui } from 'lib/nodes-ui';
import { rui } from 'lib/relationship-ui';
import CopyPaste from 'lib/copy-paste';
import { WIDTHS } from 'lib/constants.js';
import { usui } from 'lib/upsell-ui';
import { regionUI } from 'lib/region-ui';
import { getTrackingLabel } from 'lib/util';
import EventTracker from 'lib/event-tracking';

class MenuUI {
  setCanvas(canvas) {
    this.canvas = canvas;
  }

  registerMenuButton(tag,identifier) {
    canvas.menuButtons[identifier] = tag;
  }

  registerMenuItem(tag,identifier) {
    canvas.menuItems[identifier] = tag;
  }

  getMenuItem(identifier) {
    return canvas.menuItems[identifier];
  }

  getMenuButton(identifier) {
    return canvas.menuButtons[identifier];
  }

  chromeMenusAreOpen() {
    if (canvas.creationToolsTag.moreToolsOpen) {
      return true;
    }

    if (canvas.appHeaderTag.isOpen) {
      return true;
    }
    if (canvas.perspectiveSettingsMenu.summoned) {
      return true;
    }

    if (canvas.presence && canvas.presence.menuOpen) return true;
    const userAccountDropdown = document.querySelector('.user-account > .dropdown');
    if (userAccountDropdown && userAccountDropdown.classList.contains('open')) return true;

    return false;
  }

  /* layout */
  setDisplayedLayout(layout) {
    if (layout == 'layout-freehand') {
      this.refs.layoutIcon.classList.remove('layout-vertical');
      this.refs.layoutIcon.classList.remove('layout-horizontal');
      this.refs.layoutIcon.classList.add('layout-freehand');
    } else if (layout == 'layout-col') {
      this.refs.layoutIcon.classList.remove('layout-vertical');
      this.refs.layoutIcon.classList.add('layout-horizontal');
      this.refs.layoutIcon.classList.remove('layout-freehand');
    } else {
      this.refs.layoutIcon.classList.add('layout-vertical');
      this.refs.layoutIcon.classList.remove('layout-horizontal');
      this.refs.layoutIcon.classList.remove('layout-freehand');
    }
  }

  freeLinkNode() {
    const { canvas } = this;
    const operationMessages = [];

    nui.bulkEmancipateLinkNodes(canvas.getSelectedNodes(), operationMessages);

    canvas.operation.batchOperation({
      messages: operationMessages,
      onFailure: (err) => {
        console.log(`freeLinkNode batch failure: ${err}`);
      }
    });
  }

  detachParts(nodeIds) {
    if (!nodeIds || !nodeIds.length ) {
      return;
    }
    const nodes = nodeIds.map(id => canvas.store.getNode(id));
    const operationMessages = [];
    nui.bulkEmancipateParts(nodes,operationMessages);
    mui.canvas.operation.batchOperation({
      messages: operationMessages,
      onFailure: (err) => {
        console.log(`detachParts batch failure: ${err}`);
      }
    });
  }

  addCard(e) {
    e.stopPropagation();
    e.preventDefault();

    if (this.parent.getActiveLinkTag && this.parent.getActiveLinkTag()) {

      const linkTag = this.parent.getActiveLinkTag();
      rui.addCardsToLinks( [linkTag.id]);

    } else if (this.parent.getSelectedLinkIds().length) {
      rui.addCardsToLinks( this.parent.getSelectedLinkIds() );
    }
    this.parent.hide();
  }

  /* relationship-style */
  _updateRelationshipStyleButtons() {
    if (mui.canvas.menuItems.relationshipStyle) {
      mui.canvas.menuItems.relationshipStyle.update();
    }
    if (mui.canvas.menuButtons.relationshipStyle) {
      mui.canvas.menuButtons.relationshipStyle.update();
    }
  }

  setRelationshipColor(color) {
    const linkIds = this.parent.getSelectedLinkIds();
    if (linkIds) {
      rui.setColor(color,linkIds);
    }

    mui._updateRelationshipStyleButtons();
  }

  setLineType(lineType) {
    const linkIds = this.parent.getSelectedLinkIds();
    if (linkIds) {
      rui.setLineType(lineType,linkIds);
    }
    mui._updateRelationshipStyleButtons();
  }

  setLineWidth(lineWidth) {
    const linkIds = this.parent.getSelectedLinkIds();
    if (linkIds) {
      rui.setLineWidth(lineWidth,linkIds);
    }
    mui._updateRelationshipStyleButtons();
  }

  getBulkColor() {
    const linkIds = this.parent.getSelectedLinkIds();
    if (linkIds) {
      return rui.getBulkLinkColor(linkIds);
    }
  }
  getBulkLineType() {
    const linkIds = this.parent.getSelectedLinkIds();
    if (linkIds) {
      return rui.getBulkLineType(linkIds);
    }
  }
  getBulkLineWidth() {
    const linkIds = this.parent.getSelectedLinkIds();
    if (linkIds) {
      return rui.getBulkLineWidth(linkIds);
    }
  }

  getLinePreviewColor() {
    return rui.getColorPreviewStyle( this.parent.getSelectedLinkIds() );
  }

  getLinePreviewStyle() {
    return rui.getLinePreviewStyle( this.parent.getSelectedLinkIds() );
  }

  getLinePreviewWidth() {
    return rui.getLinePreviewWidth( this.parent.getSelectedLinkIds() );
  }

  /* straighten-line */
  straightenLines() {
    const links = mui.canvas.getSelectedLinks();
    const operationMessages = [];
    for (const link of links) {
      rui.straightenLine(link,operationMessages);
    }
    canvas.operation.batchOperation({
      messages: operationMessages,
      onFailure: (err) => {
        console.log(`straightenLines batch failure: ${err}`);
      }
    });
    if (!this.parent.menuName == 'toolbar') {
      this.parent.hide();
    }
  }

  addPart() {
    const { canvas } = this;

    const node = canvas.getSelectedNodes()[0];
    if (!node) return;

    const nodeTag = canvas.nodeTags[node.id];
    if (!nodeTag) return;

    nodeTag.addPart(undefined, undefined, false, true);
  }

  duplicateSelection() {
    const { canvas } = mui;
    if (usui.userIsOverLimit()) {
      canvas.showPaymentModal();
      return;
    }
    const selectedNodeIds = this.parent.getSelectedNodeIds() || [];
    const selectedLinkIds = this.parent.getSelectedLinkIds() || [];
    const selectedNodes = selectedNodeIds.map(id => canvas.store.getNode(id));
    if (!selectedNodes.length) return;
    const nodes = nui.getHighestLevelNodesInList(selectedNodes);
    const [et, el, eb, er] = canvas.getContentExtents({nodes});

    // if user right clicked an element that wasn't selected, need to change the selection
    // (after duplication, the new elements will be selected anyway)
    canvas.clearSelectedElements();
    canvas.setSelectedElements( selectedNodeIds.map(id => canvas.nodeTags[id]) );
    canvas.setSelectedElements( selectedLinkIds.map(id => canvas.linkTags[id]), true );

    CopyPaste.pasteData({
      canvas,
      data: CopyPaste.copyData({ canvas }),
      pasteWithinEnabled: false,
      inPlace: false,
      position: appUI.mapCoordsToViewportCoords({
        x: el,
        y: eb + 50
      })
    });

    // if most nodes are out of the viewport, center in view
    const newSelection = nui.getHighestLevelNodesInList(selectedNodes);
    if (!appUI.areNodesMostlyInView(newSelection)) {
      if (appUI.contentFitsInViewport(newSelection)) {
        // if the content fits in the view at the current scale, then move as little as possible
        appUI.followContent({ nodes: newSelection }, { scale: canvas.getMatrix().scale, animate: true });
      } else {
        canvas.centerContent({ nodes: newSelection }, { animate: true });
      }
    }
    if (this.parent.menuName !== 'toolbar') {
      this.parent.hide();
    }
  }

  summonImageModal() {
    const { canvas } = this;

    const node = canvas.getSelectedNodes()[0];
    if (!node) return;

    if (node.attachedFile) {
      canvas.imageModal.triggerEdit(node.id);
    } else {
      canvas.imageModal.show(node.id);
    }
  }

  createNode() {
    const { canvas } = this;
    const { scale, tx, ty } = canvas.getMatrix();
    canvas.lastNodeCreatedWithEnterKey = null;
    canvas.createNewNode({
      x: ((window.innerWidth / 2) - tx - (64 * scale)) / scale,
      y: ((window.innerHeight / 2) - ty - (24 * scale)) / scale,
    });
    canvas.appFooterTag.updateStyle();
  }

  getCardSelectedColor() {
    const nodeIds = this.parent.getSelectedNodeIds();
    if (!nodeIds) return '';
    const color = nui.getBulkCardColor(nodeIds);
    if (color) {
      return color;
    }
    return '#39424e';
  }

  getCardSelectedStyle() {
    const nodeIds = this.parent.getSelectedNodeIds();
    if (!nodeIds) return '';
    const style = nui.getBulkCardStyle(nodeIds);
    if (style) {
      return style;
    }
    return 'standard';
  }


  _updateCardStyleButtons() {
    if (mui.canvas.menuItems.cardStyle) {
      mui.canvas.menuItems.cardStyle.update();
    }
    if (mui.canvas.menuButtons.cardStyle) {
      mui.canvas.menuButtons.cardStyle.update();
    }
    mui.canvas.nodeMenuTag.update();
  }
  _updateRegionStyleButtons() {
    if (mui.canvas.menuItems.editRegionColor) {
      mui.canvas.menuItems.editRegionColor.update();
    }
    if (mui.canvas.menuButtons.editRegionColor) {
      mui.canvas.menuButtons.editRegionColor.update();
    }
  }

  _updateRegionShapeButtons() {
    mui.canvas.regionShapeMenu.update();
  }

  setCardColor(color, nodeIds) {
    nodeIds = nodeIds || this.parent.getSelectedNodeIds();
    if (nodeIds) {

      const unstyledNodeIds = [];

      for (const node of nodeIds.map(id => mui.canvas.store.getNode(id))) {
        if (!node.displayStyle) {
          unstyledNodeIds.push(node.id);
        }
      }

      if (unstyledNodeIds.length) {
        nui.setCardDisplayStyleAndColor({ style: 'accent', color, nodeIds });
      } else {
        nui.setCardColor(color,nodeIds);
      }
    }
    mui._updateCardStyleButtons();
  }

  setDisplayStyle({style}) {
    const nodeIds = this.parent.getSelectedNodeIds();
    if (nodeIds) {
      nui.setDisplayStyle(style,nodeIds);
    }
    mui._updateCardStyleButtons();
  }

  setRegionShape(shape) {
    const regionIds = this.parent.getSelectedRegionIds();

    if (regionIds && regionIds.length) {
      regionUI.setRegionShape({ ids: regionIds, shape });
      mui._updateRegionShapeButtons();
    }

    EventTracker.track({
      category: getTrackingLabel(this.parent),
      action: 'Set Region Shape',
      label: 'Selection',
    });
  }

  getSelectedRegionsShape() {
    const regionIds = this.parent.getSelectedRegionIds();
    if (!regionIds || !regionIds.length) return '';
    return regionUI.getBulkRegionShape(regionIds) || '';
  }

  /* region color */

  setRegionColor(color) {
    const regionIds = this.parent.getSelectedRegionIds();
    if (regionIds && regionIds.length) {
      regionUI.setRegionColor({ids: regionIds, color });
      mui._updateRegionStyleButtons();
    }
    EventTracker.track({
      category: getTrackingLabel(this.parent),
      action: 'Set Region Color',
      label: 'Selection',
    });
  }

  getRegionSelectedColor() {
    const regionIds = this.parent.getSelectedRegionIds();
    if (!regionIds || !regionIds.length) return '';
    const color = regionUI.getBulkRegionColor(regionIds);
    if (color) {
      return color;
    }
    return '#acb9c8';
  }

  /* pop in/out part */

  showPopIn() {
    if (!this.getSelectedNodeIds()) {
      return false;
    }
    const nodes = this.getSelectedNodeIds().map(id => this.opts.canvas.store.getNode(id));
    const filtered = nodes.filter(n => n.children().length);
    const poppedOut = filtered.filter(n => n.childrenDetached);
    if (poppedOut.length) {
      return true;
    }
    return false;
  }

  showPopOut() {
    if (!this.getSelectedNodeIds()) {
      return false;
    }
    const nodes = this.getSelectedNodeIds().map(id => this.opts.canvas.store.getNode(id));
    const filtered = nodes.filter(n => n.children().length);
    const poppedIn = filtered.filter(n => !n.childrenDetached);
    if (poppedIn.length) {
      return true;
    }
    return false;
  }

  toggleChildrenAttachment(e) {
    e.stopPropagation();
    this.popOutMessages = [];
    const nodeTags = mui.canvas.nodeTags;
    const selectedNodes = this.parent.getSelectedNodeIds().map(id => mui.canvas.store.getNode(id) );
    const applicableNodes = nui.getHighestLevelNodesInList( selectedNodes );
    let keys = [];
    for (const node of applicableNodes){
      if (this.showPopIn) {
        this.popInChildren(nodeTags[node.id]);
      } else {
        this.popOutChildren(nodeTags[node.id]);
      }
    }
    if (this.popOutMessages.length) {
      canvas.operation.batchOperation({
        messages:this.popOutMessages,
        onFailure: (err) => {
          console.log(`setChildrenDetached batch failure: ${err}`);
        }
      });
    }
    this.parent.update();
  }

  popInChildren(i) {
    i.instance.setChildrenDetached(false);
    i.updateChildDetachment(i.id);
    i.updateTopLevelNeighborsLite();
    if(i.childNodesTag){
      i.childNodesTag.update();
    }
    this.popOutMessages.push(canvas.operation.messages.setChildrenDetached({
      nodeUUID: i.id,
      detached: false,
      x: i.childrenPosition.x,
      y: i.childrenPosition.y,
    }));
  }

  popOutChildren(i) {
    let width = i.instance.width;
    let x;
    let y;
    if (!i.childrenPosition || (i.childrenPosition.x === undefined || i.childrenPosition.y === undefined )) {
      x = Math.min(275,width + 40);
      y = -60;
      i.setChildrenPosition({ x, y });
    }

    i.instance.setChildrenDetached(true);
    i.updateChildDetachment(i.id);
    i.updateTopLevelNeighborsLite();
    if(i.childNodesTag){
      i.childNodesTag.update();
      i.childNodesTag.updatePosition();
      i.childNodesTag.updateShadow();
      i.childNodesTag.enableDragging();
    }
    this.popOutMessages.push(canvas.operation.messages.setChildrenDetached({
      nodeUUID: i.id,
      detached: true,
      x,
      y,
    }));
  }

  positionSelf({ selfEl, referenceEl, clickPosition, forceLeftOfMenu }) {
    const { SIDEBAR_WIDTH } = WIDTHS;

    let referenceRect;
    if (referenceEl) {
      referenceRect = referenceEl.getBoundingClientRect();
    } else if (clickPosition) {
      referenceRect = {
        top: clickPosition.y + 5,
        bottom: clickPosition.y + 5,
        left: clickPosition.x + 5,
        right: clickPosition.x + 5,
      };
    }

    const rightCanvasEdge = canvas.sidebarStatus === 'closed' ? window.innerWidth : window.innerWidth - SIDEBAR_WIDTH;
    const rect = selfEl.getBoundingClientRect();
    const positionToLeft = rightCanvasEdge - referenceRect.right < rect.width;
    const positionAbove = window.innerHeight - referenceRect.top < rect.height + 50;
    let bottom;

    if (positionAbove) {
      bottom = 50; // enough space for alignment submenu to drop down
    }
    if (forceLeftOfMenu) {
      selfEl.style = `top: ${referenceRect.top}px; right: ${window.innerWidth - referenceRect.left}px;`;
    } else if (positionAbove && positionToLeft) {
      selfEl.style = `bottom: ${bottom}px; right: ${window.innerWidth - referenceRect.left}px;`;
    } else if (positionAbove) {
      selfEl.style = `bottom: ${bottom}px; left: ${referenceRect.right}px;`;
    } else if (positionToLeft) {
      selfEl.style = `top: ${referenceRect.bottom}px; right: ${window.innerWidth - referenceRect.right}px;`;
    } else {
      selfEl.style = `top: ${referenceRect.top}px; left: ${referenceRect.right}px;`;
    }

    this.positionSubToLeft = appUI.positionSubMenuToLeft({ positionReferenceElement: selfEl });
  }
}

const mui = new MenuUI();

export { MenuUI, mui };



// WEBPACK FOOTER //
// ./starling/src/lib/menu-ui.js